/*
 * Copyright© Celligence International, LLC - 2023
 *
 * This software program is protected by copyright laws and is the property of
 * Celligence International, LLC (Celligence). Any unauthorized reproduction,
 * distribution, modification, or public display of this program, without the
 * explicit permission of Celligence is strictly prohibited. If you have gained
 * access to this program without the expressed approval of Celligence report
 * the incident to legal@celligence.com.
 *
 */

import React, { useEffect, useState, Suspense, useRef } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import SpinnerLoader from "../../Utils/SpinnerLoader";
import "./index.css";

function AttendeeList({ themeId, eventPageData, isMobile }) {
    const [goingCount, setGoingCount] = useState("");
    const [maybeGoingCount, setMaybeGoingCount] = useState("");
    const [initials, setInitials] = useState([]);
    const [initialsHover, setInitialsHover] = useState([]);
    const [initialColors, setInitialColors] = useState([]);
    const [showInitialPopup, setShowInitialPopUp] = useState(false);

    useEffect(() => {
        const apiURL = process.env.REACT_APP_API_ATTENDEE_LIST;

        const fetchData = () => {
            fetch(apiURL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    referrerSource: "ANGLTOKENLAUNCH"
                }),
            })
                .then((response) => response.json())
                .then((data) => {
                    const {
                        goingPersonCnt,
                        maybeGoingPersonCnt,
                        data: attendees,
                    } = data.records;
                    // console.log("goingPersonCnt:", maybeGoingPersonCnt);
                    setGoingCount(goingPersonCnt[0].total_going);
                    setMaybeGoingCount(maybeGoingPersonCnt[0].total_maybe_going);
                    // Exclude null values or null properties from attendees
                    const validAttendees = attendees.filter(
                        (attendee) => attendee && attendee.initials && attendee.first_name
                    );
                    // console.log("Valid Attendees:", validAttendees);
                    const attendeeInitials = validAttendees.map((attendee) =>
                        attendee.initials.toUpperCase()
                    );
                    setInitials(attendeeInitials);

                    const attendeeInitialsHover = validAttendees.map((attendee) =>
                        attendee.attendee_count > 1
                            ? attendee.first_name + " + " + (attendee.attendee_count - 1)
                            : attendee.first_name
                    );
                    setInitialsHover(attendeeInitialsHover);

                    const specificColors = [
                        "#430038",
                        "#430038",
                        "#430038",
                        "#430038",
                        "#430038",
                        "#430038",
                        "#430038",
                        "#430038",
                    ];

                    const coloredInitials = attendeeInitials.map(
                        (initial, index) => specificColors[index] || getRandomColor()
                    );

                    setInitialColors(coloredInitials);
                })
                .catch((error) => {

                });
        };

        fetchData();
    }, []);

    const getRandomColor = () => {
        const letters = "0123456789ABCDEF";
        let color = "#";
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    };

    // Ref to detect outside clicks
    const popupRef = useRef(null);

    // Use a single handler for click and touchstart
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setShowInitialPopUp(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [popupRef]);

    const handleTogglePopup = (e) => {
        e.stopPropagation();
        setShowInitialPopUp(!showInitialPopup);
    };


    const capitalizeFirstLetter = (name) => {
        if (name) {
            return name.charAt(0).toUpperCase() + name.slice(1);
        }
        return "";
    };

    const overallCount = goingCount + maybeGoingCount;

    return (
        <>
            {(goingCount > 0 || maybeGoingCount > 0) && (
                <Suspense fallback={<SpinnerLoader />}>
                    <div className="countsection">
                        <Container>
                            <Row>
                                <Col>
                                    <div className="backgroundimag">
                                        <div className="countDiv">
                                            <div className="total-going-maybe-count">
                                                {goingCount > 0 && <p className="goingnumber">{goingCount} Going</p>}
                                                {maybeGoingCount > 0 && <p className="ps-2 maybenumber">{maybeGoingCount} Maybe</p>}
                                            </div>
                                        </div>

                                        <div className="initial-boxes">
                                            {initials.slice(0, 7).map((initial, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        background: initialColors[index],
                                                    }}
                                                    className={`initial-box ${index === 6 ? "last-box" : ""}`}
                                                >
                                                    {index === 6 ? (
                                                        <>
                                                            <div
                                                                className="overall-count"
                                                                onClick={handleTogglePopup}
                                                            >
                                                                +{overallCount - 6}
                                                            </div>
                                                            {showInitialPopup && (
                                                                <div className="initialPopupContainer" id="initialPopupContainer" ref={popupRef}>
                                                                    <div className="scrollable-content">
                                                                        {initialsHover
                                                                            .slice(6)
                                                                            .map((hoverText, i) => (
                                                                                <p key={i}>{capitalizeFirstLetter(hoverText)}</p>
                                                                            ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {capitalizeFirstLetter(initial)}
                                                            <span className="tooltipText">
                                                                {capitalizeFirstLetter(initialsHover[index])}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                </Suspense>
            )}
        </>

    );
}

export default AttendeeList;